import {
  ModuleColumn,
  ModuleLink,
  ModuleSection,
  type AccentTone,
  type IconName,
} from '@tessma/design-system';

/**
 * The Finance module's landing page links.
 *
 * SAME RULE AS THE DASHBOARD'S ENTRY CARDS: a link is offered only where the
 * session holds the permission the route and the API both require, because a
 * card leading to a 403 is worse than no card. It is a COURTESY and not a
 * control — the route guard refuses the same URL typed by hand, and the server
 * refuses it again.
 *
 * THESE ARE PLACES, NOT ACTIONS, AND NOW LOOK LIKE IT. They were fifteen
 * `variant="secondary"` buttons inside four cards, so "Sales invoices" — a
 * page — had the same shape as "New invoice" — a thing to do.
 *
 * THE COMPOSITION IS THE STITCH ONE, converted rather than approximated. The
 * reference draws "Finance Modules" as five narrow columns, each a tinted mark,
 * a bold title, one line of context and its links in a list with a chevron —
 * not a grid of tiles in five stacked cards, which is what `NavTileGrid` gave
 * and which pushed the recent records two screens down the page. The group
 * icon, its tint and its one-line blurb are the reference's own, per column.
 *
 * The frozen navigation stays at twelve entries and gains nothing: these are
 * links on the page the Finance entry already leads to, and the sidebar now
 * lists the same pages from the same table.
 */

export type FinanceDestination =
  | '/finance/customers'
  | '/finance/invoices'
  | '/finance/credit-notes'
  | '/finance/receipts'
  | '/finance/receivables'
  | '/finance/suppliers'
  | '/finance/bills'
  | '/finance/supplier-credits'
  | '/finance/payments'
  | '/finance/payables'
  | '/finance/accounts'
  | '/finance/calendar'
  | '/finance/journals'
  | '/finance/ledger'
  | '/finance/trial-balance'
  | '/finance/bank-accounts'
  | '/finance/match-rules'
  | '/finance/vat';

/**
 * BANKING AND VAT ARE TWO GROUPS, NOT ONE.
 *
 * They were a single "Banking and VAT" card, which reads as one activity.
 * Reconciling a bank statement and filing a return are different jobs, done by
 * different people, on different days of the month.
 */
type Group = 'Sales' | 'Purchases' | 'Ledger' | 'Banking' | 'VAT and tax';

interface Entry {
  readonly to: FinanceDestination;
  readonly label: string;
  readonly permission: string;
  readonly group: Group;
  readonly icon: IconName;
  /**
   * The hue of the link's mark, which is DECORATION and means nothing.
   *
   * The reference gives each link in a column a different tint so a list of
   * five reads as five things rather than one paragraph. It is not a status:
   * `danger` on "Credit notes" says nothing has gone wrong, and the frozen
   * semantic tones keep their meaning everywhere a reader could mistake them
   * for one — on a chip, a delta or a segment.
   */
  readonly tone: AccentTone;
}

const ENTRIES: readonly Entry[] = [
  {
    to: '/finance/customers',
    label: 'Customers',
    permission: 'finance.customer.read',
    group: 'Sales',
    icon: 'users-three',
    tone: 'info',
  },
  {
    to: '/finance/invoices',
    label: 'Sales invoices',
    permission: 'finance.invoice.read',
    group: 'Sales',
    icon: 'tray',
    tone: 'success',
  },
  {
    to: '/finance/credit-notes',
    label: 'Credit notes',
    permission: 'finance.invoice.read',
    group: 'Sales',
    icon: 'arrows-down-up',
    tone: 'danger',
  },
  {
    to: '/finance/receipts',
    label: 'Receipts',
    permission: 'finance.invoice.read',
    group: 'Sales',
    icon: 'check-circle',
    tone: 'info',
  },
  {
    to: '/finance/receivables',
    label: 'Receivables ageing',
    permission: 'finance.invoice.read',
    group: 'Sales',
    icon: 'trend-up',
    tone: 'accent',
  },
  {
    to: '/finance/suppliers',
    label: 'Suppliers',
    permission: 'finance.supplier.read',
    group: 'Purchases',
    icon: 'users-three',
    tone: 'info',
  },
  {
    to: '/finance/bills',
    label: 'Supplier bills',
    permission: 'finance.bill.read',
    group: 'Purchases',
    icon: 'tray',
    tone: 'danger',
  },
  {
    to: '/finance/supplier-credits',
    label: 'Supplier credits',
    permission: 'finance.supplier_credit.read',
    group: 'Purchases',
    icon: 'arrows-down-up',
    tone: 'warning',
  },
  {
    to: '/finance/payments',
    label: 'Payments',
    permission: 'finance.bill.read',
    group: 'Purchases',
    icon: 'check-circle',
    tone: 'info',
  },
  {
    to: '/finance/payables',
    label: 'Payables ageing',
    permission: 'finance.bill.read',
    group: 'Purchases',
    icon: 'trend-down',
    tone: 'accent',
  },
  {
    to: '/finance/accounts',
    label: 'Chart of accounts',
    permission: 'finance.account.read',
    group: 'Ledger',
    icon: 'list',
    tone: 'info',
  },
  {
    to: '/finance/calendar',
    label: 'Financial years and periods',
    permission: 'finance.account.read',
    group: 'Ledger',
    icon: 'calendar-blank',
    tone: 'teal',
  },
  {
    to: '/finance/journals',
    label: 'Journals',
    permission: 'finance.journal.read',
    group: 'Ledger',
    icon: 'kanban',
    tone: 'info',
  },
  {
    to: '/finance/ledger',
    label: 'General ledger',
    permission: 'finance.journal.read',
    group: 'Ledger',
    icon: 'folder',
    tone: 'success',
  },
  {
    to: '/finance/trial-balance',
    label: 'Trial balance',
    permission: 'finance.report.read',
    group: 'Ledger',
    icon: 'chart-bar',
    tone: 'accent',
  },
  {
    to: '/finance/bank-accounts',
    label: 'Bank accounts',
    permission: 'finance.bank.read',
    group: 'Banking',
    icon: 'bank',
    tone: 'accent',
  },
  /*
   * THE REFERENCE'S BANKING COLUMN LISTS FIVE, AND THREE OF THEM EXIST.
   *
   * "Bank reconciliations" is this page — the rules a statement line is matched
   * by — under the name the product already gives it. "Payments" is the same
   * route the Purchases column leads to, listed twice because the reference
   * lists it twice and because it is the same page either way. "Bank
   * transactions" and "Cash flow forecast" have no page and no authoritative
   * source, so they are absent rather than a link to nothing.
   */
  {
    to: '/finance/match-rules',
    label: 'Reconciliation rules',
    permission: 'finance.match_rule.read',
    group: 'Banking',
    icon: 'check-circle',
    tone: 'success',
  },
  {
    to: '/finance/payments',
    label: 'Payments',
    permission: 'finance.bill.read',
    group: 'Banking',
    icon: 'arrows-down-up',
    tone: 'info',
  },
  /*
   * ONE LINK, NOT FIVE. The reference's VAT column lists VAT return, VAT
   * transactions, VAT settings, EC sales list and Making Tax Digital; the
   * product has the returns screen and none of the other four, and a link to a
   * page that does not exist is worse than a shorter column.
   */
  {
    to: '/finance/vat',
    label: 'VAT returns',
    permission: 'finance.vat.read',
    group: 'VAT and tax',
    icon: 'diamond',
    tone: 'danger',
  },
];

/**
 * WHAT EACH COLUMN IS, IN THE REFERENCE'S OWN WORDS.
 *
 * The mark, its tint and the one line of context are the Stitch column heads,
 * transcribed. The blurb is the only new copy on this screen and it says what
 * the column is for, which is the question a reader of five columns of links
 * actually has.
 */
const GROUP_STYLE: Record<
  Group,
  { readonly icon: IconName; readonly tone: AccentTone; readonly blurb: string }
> = {
  Sales: {
    icon: 'trend-up',
    tone: 'success',
    blurb: 'Manage customer invoicing and incoming payments.',
  },
  Purchases: {
    icon: 'trend-down',
    tone: 'danger',
    blurb: 'Manage supplier bills and outgoing payments.',
  },
  Ledger: {
    icon: 'folder',
    tone: 'info',
    blurb: 'Maintain your financial records and accounts.',
  },
  Banking: {
    icon: 'bank',
    tone: 'accent',
    blurb: 'Manage bank accounts and reconciliations.',
  },
  'VAT and tax': {
    icon: 'diamond',
    tone: 'warning',
    blurb: 'Manage VAT returns and tax compliance.',
  },
};

const GROUPS: readonly Group[] = ['Sales', 'Purchases', 'Ledger', 'Banking', 'VAT and tax'];

export function FinanceEntryCards({
  permissions,
  onOpen,
}: {
  readonly permissions: readonly string[];
  readonly onOpen: (to: FinanceDestination) => void;
}) {
  const available = ENTRIES.filter((entry) => permissions.includes(entry.permission));
  if (available.length === 0) return null;

  return (
    <ModuleSection
      icon="squares-four"
      title="Finance Modules"
      subtitle="Quick access to all finance functions."
    >
      {GROUPS.map((group) => {
        const entries = available.filter((entry) => entry.group === group);
        /*
         * A COLUMN WITH NOTHING IN IT IS ABSENT, not an empty card. The
         * reference draws five because the reference's reader holds every
         * Finance permission; a bookkeeper who may not see the ledger gets
         * four, and the grid closes the gap.
         */
        if (entries.length === 0) return null;
        const style = GROUP_STYLE[group];
        return (
          <ModuleColumn
            key={group}
            icon={style.icon}
            tone={style.tone}
            title={group}
            blurb={style.blurb}
          >
            {entries.map((entry) => (
              <ModuleLink
                key={entry.to}
                icon={entry.icon}
                tone={entry.tone}
                label={entry.label}
                onClick={() => {
                  onOpen(entry.to);
                }}
              />
            ))}
          </ModuleColumn>
        );
      })}
    </ModuleSection>
  );
}
